/* ==========================================================================
   Cross-sell slot system (XS-01 to XS-09)
   Witchy Lashes / Witchy Lashes Skin - theme "August 2026 - v1.2.1"

   One custom element, <xs-slot>, drives every placement:

     - add to cart via /cart/add.js, with the slot's id written onto the line
       as _xs_slot and _xs_variant so the sale is attributable in Shopify
     - the button state machine (idle, loading, added, in cart)
     - the hide rule, re-evaluated on every cart change from any source
     - one offer per page
     - GA4 funnel events xs_view, xs_click, xs_add, xs_hidden

   Because the element re-runs connectedCallback whenever Shopify's section
   rendering swaps its markup, nothing needs re-initialising by hand after a
   cart update.
   ========================================================================== */

(function () {
  'use strict';

  /* The snippet loads this file itself rather than the layout, so a page
     with several slots on it can request the script more than once. Run the
     setup exactly once: a second pass would wrap fetch twice and split the
     mounted-slot registry across two closures. */
  if (window.__xsSlotLoaded) return;
  window.__xsSlotLoaded = true;

  var ADDED_MS = 1500;
  var REDUCED_MOTION =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Every mounted slot on the page. Used for the one-offer-per-page rule and
     to re-evaluate all of them together on a cart change. */
  var mounted = [];

  /* --------------------------------------------------------------------
     Cart state. One read is shared by every slot on the page rather than
     each of them fetching /cart.js for itself.
     -------------------------------------------------------------------- */

  var cartPromise = null;

  function readCart(force) {
    if (force || !cartPromise) {
      cartPromise = fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .catch(function () {
          return null;
        });
    }
    return cartPromise;
  }

  function cartHandles(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.map(function (item) {
      return item.handle;
    });
  }

  /* --------------------------------------------------------------------
     Analytics. GA4 is already installed; push through gtag when it is
     there and always mirror into dataLayer so GTM can pick it up too.
     -------------------------------------------------------------------- */

  function track(name, params) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, params);
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, params));
    } catch (e) {
      /* Measurement must never break the buy path. */
    }
  }

  /* The theme's <html> carries no template- class, so the page type is
     stamped onto each slot from Liquid, which always knows it. The class
     sniff stays only as a fallback for any slot rendered without it. */
  function pageType(el) {
    if (el && el.dataset && el.dataset.xsPageType) return el.dataset.xsPageType;
    var tpl = document.documentElement.className.match(/template-([a-z0-9-]+)/);
    return tpl ? tpl[1] : 'unknown';
  }

  /* --------------------------------------------------------------------
     Cart UI refresh. Updates totals, line items and the header bubble in
     place using Shopify's section rendering, with no page reload.
     -------------------------------------------------------------------- */

  function sectionUrl(ids) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + '?sections=' + ids.join(',');
  }

  function swap(html, containerSelector, innerSelector) {
    var container = document.querySelector(containerSelector);
    if (!container || !html) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var source = innerSelector ? parsed.querySelector(innerSelector) : parsed.body.firstElementChild;
    var target = innerSelector ? container.querySelector(innerSelector) : container;
    if (source && target) target.innerHTML = source.innerHTML;
  }

  function refreshCartUi() {
    var wanted = [];
    var cartItems = document.getElementById('main-cart-items');
    var cartFooter = document.getElementById('main-cart-footer');
    var bubble = document.getElementById('cart-icon-bubble');
    var noteButton = document.getElementById('cart-notification-button');

    if (cartItems && cartItems.dataset.id) wanted.push(cartItems.dataset.id);
    if (cartFooter && cartFooter.dataset.id) wanted.push(cartFooter.dataset.id);
    if (bubble) wanted.push('cart-icon-bubble');
    if (noteButton) wanted.push('cart-notification-button');

    if (!wanted.length) return Promise.resolve();

    return fetch(sectionUrl(wanted), { credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (sections) {
        if (!sections) return;
        if (cartItems && sections[cartItems.dataset.id]) {
          swap(sections[cartItems.dataset.id], '#main-cart-items', '.js-contents');
        }
        if (cartFooter && sections[cartFooter.dataset.id]) {
          /* .js-contents holds the subtotal, discounts and tax note. */
          swap(sections[cartFooter.dataset.id], '#main-cart-footer', '.js-contents');
        }
        if (bubble && sections['cart-icon-bubble']) {
          swap(sections['cart-icon-bubble'], '#cart-icon-bubble', null);
        }
        if (noteButton && sections['cart-notification-button']) {
          var parsed = new DOMParser().parseFromString(sections['cart-notification-button'], 'text/html');
          var el = parsed.querySelector('#cart-notification-button');
          if (el) noteButton.innerHTML = el.innerHTML;
        }
      })
      .catch(function () {
        /* Leave the page as it is rather than reloading under her. */
      });
  }

  /* --------------------------------------------------------------------
     Re-evaluate every mounted slot. Called after any cart mutation from
     any source, ours or the theme's.
     -------------------------------------------------------------------- */

  var reevaluating = false;

  function reevaluateAll() {
    if (reevaluating) return;
    reevaluating = true;
    readCart(true)
      .then(function (cart) {
        var handles = cartHandles(cart);
        mounted.forEach(function (slot) {
          slot.applyCartState(handles);
        });
      })
      .finally(function () {
        reevaluating = false;
      });
  }

  /* Catch cart mutations made by the theme, apps, or us. Wrapping fetch is
     the only way to see all of them from one place on this theme version. */
  var nativeFetch = window.fetch;
  window.fetch = function () {
    var url = arguments[0];
    var href = typeof url === 'string' ? url : url && url.url ? url.url : '';
    var isCartWrite = /\/cart\/(add|change|update|clear)(\.js)?/.test(href);
    var result = nativeFetch.apply(this, arguments);
    if (isCartWrite) {
      result
        .then(function (response) {
          if (response && response.ok) setTimeout(reevaluateAll, 0);
          return response;
        })
        .catch(function () {});
    }
    return result;
  };

  /* --------------------------------------------------------------------
     The element
     -------------------------------------------------------------------- */

  var ONE_OFFER_WARNED = false;

  function XsSlot() {
    return Reflect.construct(HTMLElement, [], XsSlot);
  }

  XsSlot.prototype = Object.create(HTMLElement.prototype);
  XsSlot.prototype.constructor = XsSlot;
  Object.setPrototypeOf(XsSlot, HTMLElement);

  XsSlot.prototype.connectedCallback = function () {
    if (this.dataset.xsReady === 'true') return;
    this.dataset.xsReady = 'true';

    this.slotId = this.dataset.xsSlot || '';
    this.variantLabel = this.dataset.xsVariantLabel || '';
    this.productHandle = this.dataset.xsProductHandle || '';
    this.variantId = this.dataset.xsVariantId || '';
    this.hideHandles = (this.dataset.xsHideIfInCart || '')
      .split(',')
      .map(function (h) {
        return h.trim();
      })
      .filter(Boolean);
    this.usesHideRule = this.dataset.xsHideRule === 'true';

    this.button = this.querySelector('[data-xs-add]');
    this.liveRegion = this.querySelector('[data-xs-live]');

    if (!this.enforceOneOfferPerPage()) return;

    mounted.push(this);

    if (this.button) {
      this.onClick = this.handleClick.bind(this);
      this.button.addEventListener('click', this.onClick);
    }

    this.querySelectorAll('[data-xs-link]').forEach(
      function (link) {
        link.addEventListener(
          'click',
          function () {
            track('xs_click', this.eventParams());
          }.bind(this)
        );
      }.bind(this)
    );

    this.observeView();

    /* The cart page renders the hide rule server side, so the first paint
       is already correct. Everywhere else (notably the add-to-cart
       notification, which the theme does not re-render) it is resolved
       here. */
    readCart(false).then(
      function (cart) {
        this.applyCartState(cartHandles(cart));
      }.bind(this)
    );
  };

  XsSlot.prototype.disconnectedCallback = function () {
    var i = mounted.indexOf(this);
    if (i > -1) mounted.splice(i, 1);
    if (this.viewObserver) this.viewObserver.disconnect();
  };

  /* One offer per page. The first slot to mount claims the page; any later
     slot pointing at a different product does not render. In the theme
     editor it says why instead of disappearing silently. */
  XsSlot.prototype.enforceOneOfferPerPage = function () {
    /* Page chrome (the menu drawer strip) neither claims the page's offer
       nor is suppressed by it. It renders before the main content, so
       letting it claim would let a nav promo hide the real in-page offer. */
    if (this.dataset.xsChrome === 'true') return true;
    var claimed = document.documentElement.dataset.xsOfferHandle;
    if (!claimed) {
      document.documentElement.dataset.xsOfferHandle = this.productHandle;
      return true;
    }
    if (claimed === this.productHandle) return true;

    this.hide('one_offer_per_page');

    if (window.Shopify && window.Shopify.designMode && !ONE_OFFER_WARNED) {
      ONE_OFFER_WARNED = true;
      var warning = document.createElement('div');
      warning.className = 'xs-warning';
      warning.textContent =
        'Cross-sell slot ' +
        this.slotId +
        ' is not showing. Another enabled slot on this page already offers a different product (' +
        claimed +
        '). Keep one offer per page: switch one of the two off in Content > Metaobjects > Cross-sell slot.';
      this.parentNode.insertBefore(warning, this);
      this.removeAttribute('hidden');
      this.style.display = 'none';
    }
    return false;
  };

  XsSlot.prototype.eventParams = function () {
    return {
      slot_id: this.slotId,
      variant_label: this.variantLabel,
      product_handle: this.productHandle,
      page_type: pageType(this)
    };
  };

  /* xs_view fires when the slot is actually on screen, not merely in the
     DOM, so view-to-add rate compares like with like across positions. */
  XsSlot.prototype.observeView = function () {
    if (this.viewed || typeof IntersectionObserver === 'undefined') return;
    this.viewObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(
          function (entry) {
            if (!entry.isIntersecting || this.viewed || this.hasAttribute('hidden')) return;
            this.viewed = true;
            this.viewObserver.disconnect();
            track('xs_view', this.eventParams());
          }.bind(this)
        );
      }.bind(this),
      { threshold: 0.5 }
    );
    this.viewObserver.observe(this);
  };

  XsSlot.prototype.hide = function (reason) {
    if (this.hasAttribute('hidden')) return;
    this.setAttribute('hidden', '');
    track('xs_hidden', Object.assign({ reason: reason }, this.eventParams()));
  };

  XsSlot.prototype.show = function () {
    if (!this.hasAttribute('hidden')) return;
    this.removeAttribute('hidden');
    if (!REDUCED_MOTION) {
      this.classList.add('xs-slot--enter');
      setTimeout(
        function () {
          this.classList.remove('xs-slot--enter');
        }.bind(this),
        300
      );
    }
    this.observeView();
  };

  /* The hide rule, plus the "In your cart" state for the components that
     stay visible when she already owns one. */
  XsSlot.prototype.applyCartState = function (handles) {
    var owned = this.hideHandles.some(function (h) {
      return handles.indexOf(h) > -1;
    });

    if (this.usesHideRule) {
      var cartIsEmpty = handles.length === 0;
      var mustHide = owned || (this.dataset.xsHideWhenCartEmpty === 'true' && cartIsEmpty);
      if (mustHide) {
        this.hide(owned ? 'in_cart' : 'cart_empty');
      } else {
        this.show();
      }
      return;
    }

    if (!this.button) return;
    var inCart = handles.indexOf(this.productHandle) > -1;
    if (inCart && this.button.dataset.xsState !== 'added') {
      this.setButtonState('incart');
    } else if (!inCart && this.button.dataset.xsState === 'incart') {
      this.setButtonState('idle');
    }
  };

  XsSlot.prototype.setButtonState = function (state) {
    if (!this.button) return;
    var labels = {
      idle: this.button.dataset.xsLabelIdle,
      loading: this.button.dataset.xsLabelLoading || 'Adding',
      added: this.button.dataset.xsLabelAdded || 'Added',
      incart: this.button.dataset.xsLabelIncart || 'In your cart'
    };
    this.button.dataset.xsState = state;
    var label = this.button.querySelector('[data-xs-label]');
    if (label) label.textContent = labels[state] || labels.idle;
    this.button.disabled = state === 'loading' || state === 'incart';

    if (this.liveRegion && (state === 'added' || state === 'incart')) {
      this.liveRegion.textContent = labels[state];
    }
  };

  XsSlot.prototype.handleClick = function (event) {
    event.preventDefault();
    if (!this.button || this.button.dataset.xsState === 'loading' || this.button.dataset.xsState === 'incart') {
      return;
    }
    if (!this.variantId) return;

    track('xs_click', this.eventParams());
    this.setButtonState('loading');

    var body = {
      items: [
        {
          id: Number(this.variantId),
          quantity: 1,
          properties: {
            _xs_slot: this.slotId,
            _xs_variant: this.variantLabel
          }
        }
      ]
    };

    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

    nativeFetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    })
      .then(
        function (response) {
          if (!response.ok) throw new Error('add failed');
          return response.json();
        }.bind(this)
      )
      .then(
        function () {
          track('xs_add', this.eventParams());
          this.setButtonState('added');
          this.dataset.xsAdded = 'true';
          if (this.closest('.xs-note')) this.closest('.xs-note').dataset.xsAdded = 'true';

          return refreshCartUi().then(
            function () {
              /* The added state holds for 1.5s, then hands over to the hide
                 rule where one applies. */
              setTimeout(
                function () {
                  reevaluateAll();
                }.bind(this),
                ADDED_MS
              );
            }.bind(this)
          );
        }.bind(this)
      )
      .catch(
        function () {
          this.setButtonState('idle');
        }.bind(this)
      );
  };

  if (!window.customElements.get('xs-slot')) {
    window.customElements.define('xs-slot', XsSlot);
  }
})();
